import React from "react";
import { Inbox } from "lucide-react";

/**
 * Smart empty state with friendly message + optional CTA
 *
 * @param {React.ComponentType} icon  Lucide icon component (default: Inbox)
 * @param {string} title              Bold headline
 * @param {string} description        Friendly explanatory text
 * @param {string} actionLabel        CTA button label (optional)
 * @param {Function} onAction         CTA handler (optional)
 * @param {string} testId             data-testid for the container
 * @param {React.ReactNode} extra     Extra slot (additional buttons / hints)
 */
export default function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  actionLabel,
  onAction,
  testId,
  extra,
}) {
  return (
    <div
      className="card-surface flex flex-col items-center justify-center text-center px-6 py-12 fade-in-up"
      data-testid={testId || "empty-state"}
    >
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5 border border-[var(--border)] bg-[var(--surface-2)]">
        <Icon size={28} className="text-[var(--gold)] opacity-80" />
      </div>
      {title && (
        <h3 className="font-heading text-lg text-[var(--text)] mb-1.5">{title}</h3>
      )}
      {description && (
        <p className="text-sm text-[var(--text-muted)] max-w-md leading-relaxed">{description}</p>
      )}
      {(actionLabel || extra) && (
        <div className="flex items-center gap-2 mt-5">
          {actionLabel && (
            <button
              onClick={onAction}
              className="btn-gold px-5 py-2 rounded-md text-sm"
              data-testid={testId ? `${testId}-action` : "empty-state-action"}
            >
              {actionLabel}
            </button>
          )}
          {extra}
        </div>
      )}
    </div>
  );
}
